import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { SUPPORT_EMAIL } from '../../util/mailto';
import { organizationRef } from '../../util/siteStructuredData';

import { Page, H1, LayoutSingleColumn, NamedLink, ExternalLink } from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SupportPage.module.css';

// Die eine Kontaktadresse steht in util/mailto.js; hier weitergereicht, weil der Test sie von der
// Seite importiert.
export { SUPPORT_EMAIL };

/**
 * Self-service topics. Each entry renders as a card; `linkName` is a route name from
 * routeConfiguration, so a renamed route breaks the build rather than shipping a dead link.
 * Entries without `linkName` are answered inline (the steps happen inside the app).
 */
const TOPICS = [
  { key: 'booking', linkName: 'SearchPage' },
  { key: 'cancel' },
  { key: 'payment' },
  { key: 'account' },
  { key: 'privacy', linkName: 'PrivacyPolicyPage' },
  { key: 'report' },
  { key: 'host', linkName: 'SpaceOwnerLandingPage' },
];

const SupportPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const [openTopic, setOpenTopic] = useState(null);

  const pageTitle = intl.formatMessage({ id: 'SupportPage.meta.title' });
  const pageDescription = intl.formatMessage({ id: 'SupportPage.meta.description' });

  const marketplaceRootURL = config.marketplaceRootURL.replace(/\/$/, '');
  const supportPath = createResourceLocatorString('SupportPage', routeConfiguration, {}, {});
  const supportUrl = `${marketplaceRootURL}${supportPath}`;

  const schema = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': `${supportUrl}#webpage`,
        url: supportUrl,
        name: pageTitle,
        description: pageDescription,
        inLanguage: intl.locale,
        // The one Organization (name, legal name, address, contact) is described on the home page;
        // a second description here used to disagree with it ("Organization described 2 ways").
        publisher: organizationRef(marketplaceRootURL),
      },
    ],
    [intl.locale, marketplaceRootURL, pageDescription, pageTitle, supportUrl]
  );

  const toggleTopic = key => setOpenTopic(prev => (prev === key ? null : key));

  return (
    <Page
      className={css.root}
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      schema={schema}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.content}>
          <section className={css.hero}>
            <p className={css.eyebrow}>
              <FormattedMessage id="SupportPage.eyebrow" />
            </p>
            <H1 as="h1" className={css.title}>
              <FormattedMessage id="SupportPage.title" />
            </H1>
            <p className={css.lead}>
              <FormattedMessage id="SupportPage.lead" />
            </p>
          </section>

          <section className={css.contactGrid} aria-labelledby="support-contact-heading">
            <h2 id="support-contact-heading" className={css.visuallyHidden}>
              <FormattedMessage id="SupportPage.contactHeading" />
            </h2>

            <ExternalLink href={`mailto:${SUPPORT_EMAIL}`} className={css.contactCardPrimary}>
              <span className={css.contactLabel}>
                <FormattedMessage id="SupportPage.contactSupportLabel" />
              </span>
              <span className={css.contactValue}>{SUPPORT_EMAIL}</span>
              <span className={css.contactHint}>
                <FormattedMessage id="SupportPage.contactSupportHint" />
              </span>
            </ExternalLink>

            <div className={css.contactCard}>
              <span className={css.contactLabel}>
                <FormattedMessage id="SupportPage.responseTimeLabel" />
              </span>
              <span className={css.contactValue}>
                <FormattedMessage id="SupportPage.responseTimeValue" />
              </span>
              <span className={css.contactHint}>
                <FormattedMessage id="SupportPage.responseTimeHint" />
              </span>
            </div>

            <div className={css.contactCard}>
              <span className={css.contactLabel}>
                <FormattedMessage id="SupportPage.contactDetailsLabel" />
              </span>
              <span className={css.contactValue}>
                <FormattedMessage id="SupportPage.contactDetailsValue" />
              </span>
              <span className={css.contactHint}>
                <FormattedMessage id="SupportPage.contactDetailsHint" />
              </span>
            </div>
          </section>

          <section className={css.section} aria-labelledby="support-topics-heading">
            <h2 id="support-topics-heading" className={css.sectionTitle}>
              <FormattedMessage id="SupportPage.topicsHeading" />
            </h2>
            <p className={css.sectionLead}>
              <FormattedMessage id="SupportPage.topicsLead" />
            </p>

            <dl className={css.topicList}>
              {TOPICS.map(({ key, linkName }) => {
                const isOpen = openTopic === key;
                return (
                  <div key={key} className={css.topicItem}>
                    <dt className={css.topicDt}>
                      <button
                        type="button"
                        id={`support-topic-${key}`}
                        className={css.topicButton}
                        aria-expanded={isOpen}
                        aria-controls={`support-topic-${key}-answer`}
                        onClick={() => toggleTopic(key)}
                      >
                        <FormattedMessage id={`SupportPage.topic.${key}.question`} />
                        <span className={css.topicIcon} aria-hidden="true">
                          {isOpen ? '−' : '+'}
                        </span>
                      </button>
                    </dt>
                    <dd
                      id={`support-topic-${key}-answer`}
                      className={classNames(css.topicAnswerWrap, {
                        [css.topicAnswerWrapOpen]: isOpen,
                      })}
                      aria-labelledby={`support-topic-${key}`}
                    >
                      {/* Zwei Ebenen: die äussere klappt (0fr → 1fr), die innere trägt das
                          Padding. Ohne diese Trennung bliebe das Padding im geschlossenen
                          Zustand als Leerstreifen stehen. */}
                      <div className={css.topicAnswerClip}>
                        <div className={css.topicAnswer}>
                          <FormattedMessage id={`SupportPage.topic.${key}.answer`} />
                          {linkName ? (
                            <NamedLink name={linkName} className={css.topicLink}>
                              <FormattedMessage id={`SupportPage.topic.${key}.linkLabel`} />
                            </NamedLink>
                          ) : null}
                        </div>
                      </div>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>

          <section
            id="melden"
            className={css.reportSection}
            aria-labelledby="support-report-heading"
          >
            <h2 id="support-report-heading" className={css.sectionTitle}>
              <FormattedMessage id="SupportPage.reportHeading" />
            </h2>
            <p className={css.reportLead}>
              <FormattedMessage id="SupportPage.reportLead" />
            </p>

            <ol className={css.steps}>
              <li className={css.step}>
                <span className={css.stepNumber} aria-hidden="true">
                  1
                </span>
                <span className={css.stepText}>
                  <FormattedMessage id="SupportPage.reportStep1" />
                </span>
              </li>
              <li className={css.step}>
                <span className={css.stepNumber} aria-hidden="true">
                  2
                </span>
                <span className={css.stepText}>
                  <FormattedMessage id="SupportPage.reportStep2" />
                </span>
              </li>
              <li className={css.step}>
                <span className={css.stepNumber} aria-hidden="true">
                  3
                </span>
                <span className={css.stepText}>
                  <FormattedMessage id="SupportPage.reportStep3" />
                </span>
              </li>
            </ol>

            <p className={css.reportFootnote}>
              <FormattedMessage
                id="SupportPage.reportFootnote"
                values={{
                  termsLink: (
                    <NamedLink name="TermsOfServicePage" className={css.inlineLink}>
                      <FormattedMessage id="SupportPage.reportFootnoteTermsLabel" />
                    </NamedLink>
                  ),
                  mailLink: (
                    <ExternalLink href={`mailto:${SUPPORT_EMAIL}`} className={css.inlineLink}>
                      {SUPPORT_EMAIL}
                    </ExternalLink>
                  ),
                }}
              />
            </p>
          </section>

          <section className={css.section} aria-labelledby="support-more-heading">
            <h2 id="support-more-heading" className={css.sectionTitle}>
              <FormattedMessage id="SupportPage.moreHeading" />
            </h2>
            <ul className={css.linkRow}>
              <li>
                <NamedLink name="FAQLandingPage" className={css.linkCard}>
                  <FormattedMessage id="SupportPage.linkFaq" />
                </NamedLink>
              </li>
              <li>
                <NamedLink name="TermsOfServicePage" className={css.linkCard}>
                  <FormattedMessage id="SupportPage.linkTerms" />
                </NamedLink>
              </li>
              <li>
                <NamedLink name="PrivacyPolicyPage" className={css.linkCard}>
                  <FormattedMessage id="SupportPage.linkPrivacy" />
                </NamedLink>
              </li>
            </ul>
          </section>

          <section className={css.operator} aria-labelledby="support-operator-heading">
            <h2 id="support-operator-heading" className={css.operatorTitle}>
              <FormattedMessage id="SupportPage.operatorHeading" />
            </h2>
            <address className={css.address}>
              Pinnacle Interactive GmbH
              <br />
              Herzogstrasse 10
              <br />
              5000 Aarau
              <br />
              <FormattedMessage id="SupportPage.operatorCountry" />
              <br />
              <ExternalLink href={`mailto:${SUPPORT_EMAIL}`} className={css.inlineLink}>
                {SUPPORT_EMAIL}
              </ExternalLink>
            </address>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

SupportPageComponent.propTypes = {
  scrollingDisabled: bool,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const SupportPage = compose(connect(mapStateToProps))(SupportPageComponent);

export { SupportPageComponent };
export default SupportPage;
